import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Utensils,
  ShoppingCart,
  Lightbulb,
  Clock,
  Flame,
  Loader2,
  ChefHat
} from 'lucide-react';

// Use relative path for API calls to avoid CORS issues
const API = '/api';

const WeeklyPlan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [formData, setFormData] = useState({
    goal: 'maintain',
    diet_type: 'normal',
    allergies: [],
    excluded_foods: [],
    calorie_target: ''
  });

  const dietTypes = [
    { value: 'normal', label: 'Normal (Dengeli)' },
    { value: 'vegetarian', label: 'Vejetaryen' },
    { value: 'vegan', label: 'Vegan' },
    { value: 'pescatarian', label: 'Pesketaryen' },
    { value: 'keto', label: 'Ketojenik' },
    { value: 'gluten_free', label: 'Glutensiz' }
  ];

  const allergyOptions = [
    'Süt ürünleri', 'Yumurta', 'Fıstık', 'Kabuklu deniz ürünleri',
    'Gluten', 'Soya', 'Balık', 'Kuruyemiş'
  ];

  const handleAllergyToggle = (allergy) => {
    setFormData(prev => ({
      ...prev,
      allergies: prev.allergies.includes(allergy)
        ? prev.allergies.filter(a => a !== allergy)
        : [...prev.allergies, allergy]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/weekly-plan`,
        {
          goal: formData.goal,
          diet_type: formData.diet_type,
          allergies: formData.allergies,
          excluded_foods: formData.excluded_foods.length > 0 ? formData.excluded_foods.split(',').map(f => f.trim()) : [],
          calorie_target: formData.calorie_target ? parseInt(formData.calorie_target) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPlan(response.data.plan);
      toast.success('Beslenme planınız hazır!');
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || 'Haftalık limitinize ulaştınız');
      } else if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Plan oluşturulurken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const daysOfWeek = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-green-50 via-white to-green-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/ai-dietitian')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          AI Diyetisyen'e Dön
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Calendar className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Haftalık Beslenme Planı</h1>
          <p className="text-gray-600">AI destekli kişiselleştirilmiş Türk mutfağı beslenme programı</p>
        </div>

        {!plan ? (
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Plan Tercihleri</CardTitle>
              <CardDescription>Beslenme planınızı özelleştirin</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Hedefiniz</Label>
                    <Select
                      value={formData.goal}
                      onValueChange={(value) => setFormData({ ...formData, goal: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lose">Kilo Vermek</SelectItem>
                        <SelectItem value="maintain">Kilomu Korumak</SelectItem>
                        <SelectItem value="gain">Kilo Almak</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Diyet Tipi</Label>
                    <Select
                      value={formData.diet_type}
                      onValueChange={(value) => setFormData({ ...formData, diet_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dietTypes.map((diet) => (
                          <SelectItem key={diet.value} value={diet.value}>
                            {diet.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Günlük Kalori Hedefi (opsiyonel)</Label>
                  <Input
                    type="number"
                    placeholder="Örn: 1800"
                    value={formData.calorie_target}
                    onChange={(e) => setFormData({ ...formData, calorie_target: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız hedefinize göre otomatik hesaplanır</p>
                </div>

                <div>
                  <Label className="mb-3 block">Alerjiler</Label>
                  <div className="flex flex-wrap gap-2">
                    {allergyOptions.map((allergy) => (
                      <Badge
                        key={allergy}
                        variant={formData.allergies.includes(allergy) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handleAllergyToggle(allergy)}
                      >
                        {allergy}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>İstemediğiniz Yiyecekler (virgülle ayırın)</Label>
                  <Input
                    placeholder="Örn: brokoli, karnabahar, lahana"
                    value={formData.excluded_foods}
                    onChange={(e) => setFormData({ ...formData, excluded_foods: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Plan Oluşturuluyor... (30-60 sn)
                    </>
                  ) : (
                    <>
                      <ChefHat className="h-4 w-4 mr-2" />
                      Beslenme Planı Oluştur
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Plan Display */}
            <Tabs defaultValue="plan" className="w-full">
              <TabsList className="grid w-full grid-cols-3 max-w-md mx-auto">
                <TabsTrigger value="plan">Haftalık Plan</TabsTrigger>
                <TabsTrigger value="shopping">Alışveriş Listesi</TabsTrigger>
                <TabsTrigger value="tips">İpuçları</TabsTrigger>
              </TabsList>

              <TabsContent value="plan" className="mt-6">
                <div className="grid gap-4">
                  {(plan.weekly_plan || []).map((day, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{day.day}</CardTitle>
                          <Badge variant="outline">
                            <Flame className="h-3 w-3 mr-1" />
                            {day.total_calories} kcal
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-5 gap-3">
                          {(day.meals || []).map((meal, mealIndex) => (
                            <div key={mealIndex} className="bg-gray-50 p-3 rounded-lg">
                              <div className="flex items-center text-xs text-gray-500 mb-1">
                                <Clock className="h-3 w-3 mr-1" />
                                {meal.time} - {meal.name}
                              </div>
                              <p className="text-sm font-medium">{meal.description}</p>
                              <p className="text-xs text-orange-600 mt-1">{meal.calories} kcal</p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="shopping" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Haftalık Alışveriş Listesi
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-2">
                      {(plan.shopping_list || []).map((item, index) => (
                        <div key={index} className="flex items-center p-2 bg-gray-50 rounded">
                          <Checkbox id={`item-${index}`} />
                          <label htmlFor={`item-${index}`} className="ml-2 text-sm cursor-pointer">
                            {item}
                          </label>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="tips" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Lightbulb className="h-5 w-5 mr-2 text-yellow-500" />
                      Beslenme İpuçları
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {(plan.tips || []).map((tip, index) => (
                        <li key={index} className="flex items-start">
                          <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">
                            {index + 1}
                          </span>
                          <span className="text-gray-700">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => setPlan(null)}>
                Yeni Plan Oluştur
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/ai-dietitian/chat')}
              >
                Diyetisyene Sor
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyPlan;
